import { 
  Box, Typography, Button, Card, CardContent,
  CircularProgress, Divider, Zoom, Alert, LinearProgress, Paper
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FavoriteIcon from "@mui/icons-material/Favorite";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ReplayIcon from "@mui/icons-material/Replay";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import endpoints from "../../../../environment/endpoints";
import GenericResponse from "../../../../objects/GenericResponse";
import FetchService from "../../../../services/FetchService";
import style from "./VotingSession.module.css";

const fetchService = new FetchService();

interface PairMatchup {
  contestant1: {
    id: string;
    title: string;
    description?: string;
    imageUrl?: string;
    score: number;
  };
  contestant2: {
    id: string;
    title: string;
    description?: string;
    imageUrl?: string;
    score: number;
  };
}

export default function VotingSession() {
  const { id } = useParams();
  const mashListId = id as string;

  const [deck, setDeck] = useState<PairMatchup[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [voting, setVoting] = useState(false);
  const [listName, setListName] = useState("");
  const [viewShortKey, setViewShortKey] = useState("");

  const initSession = useCallback(async () => {
    setLoading(true);
    setError("");
    setIsCompleted(false);

    const res: GenericResponse = await fetchService.get(endpoints.getMashList, mashListId);
    if (!res.success || !res.data) {
      setError(res.errors || "Failed to load mash list.");
      setLoading(false);
      return;
    }

    setListName(res.data.name || "");
    setViewShortKey(res.data.viewShortKey || "");
    const contenders = res.data.contenders || [];
    if (contenders.length < 2) {
      setError("This mash list needs at least 2 contenders to start mashing.");
      setLoading(false);
      return;
    }

    // Build all unique pair matchups (N * (N - 1) / 2)
    const pairs: PairMatchup[] = [];
    for (let i = 0; i < contenders.length; i++) {
      for (let j = i + 1; j < contenders.length; j++) {
        const swap = Math.random() > 0.5;
        pairs.push({
          contestant1: swap ? contenders[j] : contenders[i],
          contestant2: swap ? contenders[i] : contenders[j],
        });
      }
    }

    // Fisher-Yates shuffle to randomize matchup order
    for (let i = pairs.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
    }

    setDeck(pairs);
    setCurrentIndex(0);
    setLoading(false);
  }, [mashListId]);

  useEffect(() => {
    mashListId && initSession();

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mashListId, initSession]);

  const handleVote = async (voteForOne: boolean) => {
    if (voting || isCompleted || deck.length === 0) return;

    const currentMatchup = deck[currentIndex];
    if (!currentMatchup) return;

    setVoting(true);
    const winnerId = voteForOne ? currentMatchup.contestant1.id : currentMatchup.contestant2.id;
    const loserId = voteForOne ? currentMatchup.contestant2.id : currentMatchup.contestant1.id;

    const res: GenericResponse = await fetchService.post(
      endpoints.vote,
      { WinnerId: winnerId, LoserId: loserId },
      mashListId
    );

    setVoting(false);

    if (res.success) {
      if (currentIndex + 1 >= deck.length) {
        setIsCompleted(true);
      } else {
        setCurrentIndex((prev) => prev + 1);
      }
    } else {
      alert(res.errors || "Failed to record vote.");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80vh" p={3}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button component={Link} to={`/mashlist/${viewShortKey || mashListId}`} variant="contained">
          Back to List
        </Button>
      </Box>
    );
  }

  if (isCompleted) {
    return (
      <Box className={style.container}>
        <Paper elevation={6} sx={{ p: 5, textAlign: 'center', borderRadius: 4, maxWidth: 550, width: '90%' }}>
          <EmojiEventsIcon sx={{ fontSize: 90, color: '#f59e0b', mb: 2 }} />
          <Typography variant="h3" fontWeight="900" color="#1a1a1a" gutterBottom>
            Session Complete! 🎉
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" mb={3}>
            You've completed all <strong>{deck.length}</strong> unique matchups in {listName ? `"${listName}"` : "this list"}!
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} justifyContent="center">
            <Button
              component={Link}
              to={`/mashlist/${viewShortKey || mashListId}`}
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<FormatListNumberedIcon />}
              sx={{ py: 1.5, px: 3, fontWeight: 'bold', borderRadius: '50px' }}
            >
              View Leaderboard
            </Button>

            <Button
              onClick={initSession}
              variant="outlined"
              color="secondary"
              size="large"
              startIcon={<ReplayIcon />}
              sx={{ py: 1.5, px: 3, fontWeight: 'bold', borderRadius: '50px' }}
            >
              Restart Session
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  const currentMatchup = deck[currentIndex];
  const progressPercent = Math.round(((currentIndex) / deck.length) * 100);

  return (
    <Box className={style.container}>
      <Button 
        component={Link} 
        to={`/mashlist/${viewShortKey || mashListId}`} 
        startIcon={<ArrowBackIcon />}
        color="inherit"
        sx={{ position: 'absolute', left: 20, top: 20 }}
      >
        Back to List
      </Button>

      <Box className={style.header}>
        <Typography variant="h3" sx={{ fontWeight: '900', color: '#333', mb: 0.5 }}>
          {listName || "Pick Your Winner"}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" mb={2}>
          Matchup {currentIndex + 1} of {deck.length}
        </Typography>

        <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
          <LinearProgress 
            variant="determinate" 
            value={progressPercent} 
            color="secondary"
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      </Box>

      <div className={style.ballotPaper}>
        {/* VS Badge */}
        <Box className={style.vsBadge}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>VS</Typography>
        </Box>

        {[currentMatchup?.contestant1, currentMatchup?.contestant2].map((contestant, index) => (
          <div className={style.cardSlot} key={contestant?.id || index}>
            <Zoom in={!!contestant} style={{ transitionDelay: index === 0 ? '50ms' : '100ms' }}>
              <Card 
                className={`${style.contenderCard} ${voting ? style.voting : ''}`}
                onClick={() => handleVote(index === 0)}
              >
                {contestant?.imageUrl && (
                  <div className={style.imageWrapper}>
                    <img 
                      src={contestant.imageUrl} 
                      alt={contestant.title}
                      className={style.contenderImage}
                    />
                  </div>
                )}

                <CardContent className={style.cardContent}>
                  <Typography variant="h3" className={style.contenderTitle}>
                    {contestant?.title}
                  </Typography>
                  
                  <Typography variant="body1" className={style.contenderDescription}>
                    {contestant?.description || "\u00A0"}
                  </Typography>

                  <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.15)' }} />
                  
                  <Typography variant="h5" className={style.ratingText}>
                    Current Rating: <strong>{contestant?.score}</strong>
                  </Typography>

                  <Button 
                    variant="contained" 
                    color="secondary" 
                    size="large"
                    fullWidth
                    startIcon={<FavoriteIcon />}
                    sx={{ 
                      mt: 2,
                      py: 1.5, 
                      fontSize: '1.1rem', 
                      fontWeight: 'bold',
                      borderRadius: '50px',
                      boxShadow: '0 10px 20px rgba(255,57,0,0.3)'
                    }}
                  >
                    Vote For {contestant?.title}
                  </Button>
                </CardContent>
              </Card>
            </Zoom>
          </div>
        ))}
      </div>
    </Box>
  );
}
